import { X } from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const TOOLTIP_STYLE = {
  backgroundColor: "var(--popover)",
  color: "var(--popover-foreground)",
  border: "1px solid var(--border)",
};

const SAMPLE_STEP = 12;
const sampleEveryNth = <T,>(arr: T[], n: number) =>
  arr.filter((_, index) => index % n === 0);

interface ChartsPanelProps {
  open: boolean;
  onClose: () => void;
  precomputedData: any;
  currentTimestamp: string;
  fuelTechColors: Record<string, string>;
  allFuelTypes: string[];
  onGeneratorClick: (duid: string) => void;
}

export const ChartsPanel = ({
  open,
  onClose,
  precomputedData,
  currentTimestamp,
  fuelTechColors,
  allFuelTypes,
  onGeneratorClick,
}: ChartsPanelProps) => {
  if (!open || !precomputedData) return null;

  const currentData = precomputedData.timestampData[currentTimestamp];
  if (!currentData) return null;

  const pieChartData = currentData.pieChartData;
  const topGenerators = currentData.topGenerators;
  const timeSeriesData = currentData.timeSeries24h;

  const currentEmissions = currentData.currentEmissions;
  const currentIntensity = currentData.currentIntensity;
  const emissionsByFuelType = currentData.emissionsByFuelType;

  const formatTime = (timestamp: string) => {
    const parts = timestamp.split(" ");
    if (parts.length === 2) {
      return parts[1].substring(0, 5);
    }
    return timestamp;
  };

  const sampledTimeSeriesData = sampleEveryNth(timeSeriesData, SAMPLE_STEP);

  return (
    <div className="absolute bottom-4 left-4 top-32 z-1000 w-112.5 overflow-y-auto group-has-data-[collapsible=icon]/sidebar-wrapper:top-28">
      <Card className="bg-card/95">
        <CardContent>
          <div className="mb-2 flex items-center justify-between">
            <h6 className="text-base font-medium">Market Analytics</h6>
            <Button
              variant="ghost"
              size="icon"
              aria-label="close"
              className="size-7"
              onClick={onClose}
            >
              <X className="size-5" />
            </Button>
          </div>

          {/* Current Fuel Mix - Pie Chart */}
          <div className="mb-6">
            <p className="mb-1 text-sm font-medium">
              Current Fuel Mix ({currentTimestamp})
            </p>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieChartData.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        fuelTechColors[entry.name.replace(/ /g, "_")] ||
                        "#999999"
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: any) => `${value} MW`}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Top 10 Generators - Bar Chart */}
          <div className="mb-6">
            <p className="mb-1 text-sm font-medium">Top 10 Generators</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={topGenerators}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                onClick={(data) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    const generator = data.activePayload[0].payload;
                    onGeneratorClick(generator.duid);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={90} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: any) => `${value} MW`}
                />
                <Bar dataKey="output" fill="#8884d8" cursor="pointer">
                  {topGenerators.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={fuelTechColors[entry.fueltech] || "#999999"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 24-Hour Stacked Area Chart */}
          <div className="mb-6">
            <p className="mb-1 text-sm font-medium">24-Hour Generation Mix</p>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={sampledTimeSeriesData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  interval="preserveStartEnd"
                />
                <YAxis />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: any) => `${value} MW`}
                  labelFormatter={formatTime}
                />
                <Legend />
                {allFuelTypes.map((fuelType) => (
                  <Area
                    key={fuelType}
                    type="monotone"
                    dataKey={fuelType}
                    stackId="1"
                    stroke={fuelTechColors[fuelType] || "#999999"}
                    fill={fuelTechColors[fuelType] || "#999999"}
                    name={fuelType.replace(/_/g, " ")}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* 24-Hour Total Output - Line Chart */}
          <div className="mb-6">
            <p className="mb-1 text-sm font-medium">24-Hour Total Output</p>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart
                data={sampledTimeSeriesData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  interval="preserveStartEnd"
                />
                <YAxis />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: any) => `${value} MW`}
                  labelFormatter={formatTime}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  dot={false}
                  name="Total Output"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Current Emissions Summary */}
          <div className="mb-6 rounded-md bg-foreground/5 p-4">
            <p className="mb-1 text-sm font-medium">
              Current Emissions Snapshot
            </p>
            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-xs text-muted-foreground">Emissions Rate</p>
                <p className="text-base font-medium">
                  {currentEmissions.toFixed(2)} t CO₂e/5min
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Grid Intensity</p>
                <p className="text-base font-medium">
                  {currentIntensity.toFixed(0)} kg CO₂e/MWh
                </p>
              </div>
            </div>
          </div>

          {/* Emissions by Fuel Type - Bar Chart */}
          <div className="mb-6">
            <p className="mb-1 text-sm font-medium">Emissions by Fuel Type</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={emissionsByFuelType.filter((d: any) => d.emissions > 0)}
                margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  label={{
                    value: "t CO₂e/5min",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: any) => `${value} t CO₂e/5min`}
                />
                <Bar dataKey="emissions" fill="#FF5722">
                  {emissionsByFuelType.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        fuelTechColors[entry.name.replace(/ /g, "_")] ||
                        "#999999"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 24-Hour Emissions Intensity - Line Chart */}
          <div className="mb-6">
            <p className="mb-1 text-sm font-medium">
              24-Hour Grid Emissions Intensity
            </p>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart
                data={sampledTimeSeriesData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  interval="preserveStartEnd"
                />
                <YAxis
                  label={{
                    value: "kg CO₂e/MWh",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: any) => `${value} kg CO₂e/MWh`}
                  labelFormatter={formatTime}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="intensity"
                  stroke="#FF5722"
                  strokeWidth={2}
                  dot={false}
                  name="Grid Intensity"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* 24-Hour Total Emissions - Area Chart */}
          <div className="mb-6">
            <p className="mb-1 text-sm font-medium">
              24-Hour Cumulative Emissions
            </p>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart
                data={sampledTimeSeriesData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  interval="preserveStartEnd"
                />
                <YAxis
                  label={{
                    value: "t CO₂e/5min",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: any) => `${value} t CO₂e/5min`}
                  labelFormatter={formatTime}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="emissions"
                  stroke="#D32F2F"
                  fill="#FF5722"
                  fillOpacity={0.6}
                  name="Emissions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
